import React, { useEffect } from "react";
import { FormControl, InputLabel, MenuItem, Select, Stack, TextField } from "@mui/material";
import type { EvaluationDomain, OperandDto } from "../../../../../../types/resolver";
import { CODES_CONTEXT_KEYS, CASE_CONTEXT_KEYS, SPAN_CONTEXT_KEYS, OTHER_KEY } from "./constants";
import type { OperandTypeComponentProps } from "./types";

export interface KeyOperandProps extends OperandTypeComponentProps {
    evaluationDomain?: EvaluationDomain;
}

export const KeyOperand: React.FC<KeyOperandProps> = ({
    value,
    onChange,
    evaluationDomain,
}) => {
    const keyOptions: readonly string[] =
        evaluationDomain === "CODES"
            ? CODES_CONTEXT_KEYS
            : evaluationDomain === "CASE"
              ? CASE_CONTEXT_KEYS
              : SPAN_CONTEXT_KEYS;

    const currentKey = value?.key ?? "";
    const isKnown = keyOptions.includes(currentKey as typeof keyOptions[number]);
    const selectValue = evaluationDomain ? (isKnown ? currentKey : OTHER_KEY) : currentKey;
    const defaultKey = keyOptions[0] ?? "";

    useEffect(() => {
        if (!evaluationDomain || !keyOptions.length) return;
        if (currentKey !== "" || isKnown) return;
        onChange({ ...(value ?? {}), operandType: "key", key: defaultKey } as OperandDto);
        // Sync only when key is empty and no valid key is set.
    }, [evaluationDomain, keyOptions.length]); // eslint-disable-line react-hooks/exhaustive-deps -- avoid loops from unstable callbacks

    if (evaluationDomain) {
        return (
            <Stack spacing={1}>
                <FormControl size="small" fullWidth>
                    <InputLabel id="key-context-label">Key (context path)</InputLabel>
                    <Select
                        labelId="key-context-label"
                        label="Key (context path)"
                        value={selectValue}
                        onChange={(e) => {
                            const v = e.target.value as string;
                            if (v === OTHER_KEY) {
                                onChange({ ...(value ?? {}), operandType: "key", key: "" } as OperandDto);
                            } else {
                                onChange({ ...(value ?? {}), operandType: "key", key: v } as OperandDto);
                            }
                        }}
                    >
                        {keyOptions.map((k) => (
                            <MenuItem key={k} value={k}>{k}</MenuItem>
                        ))}
                        <MenuItem value={OTHER_KEY}>Custom path…</MenuItem>
                    </Select>
                </FormControl>
                {selectValue === OTHER_KEY && (
                    <TextField
                        size="small"
                        label="Custom path"
                        value={currentKey}
                        onChange={(e) =>
                            onChange({ ...(value ?? {}), operandType: "key", key: e.target.value } as OperandDto)
                        }
                        placeholder="e.g. attributes.role, units"
                        fullWidth
                    />
                )}
            </Stack>
        );
    }

    return (
        <TextField
            size="small"
            label="Key (path)"
            value={String(value?.key ?? "")}
            onChange={(e) =>
                onChange({
                    ...(value ?? {}),
                    operandType: "key",
                    key: e.target.value,
                } as OperandDto)
            }
            placeholder="e.g. code, codeType"
            fullWidth
        />
    );
};
